import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Mail, Phone, MapPin, Clock, Send, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import SEO, { generateBreadcrumbSchema } from '../../components/SEO';
import { optimizedSrc, optimizedSrcSet } from '../../lib/image-cdn';
import { usePublicSettings } from '../../hooks/usePublicSettings';
import { SocialLinks } from '../../components/CompanyProfile';

export default function Contact() {
  const { settings } = usePublicSettings();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Client-side validation (backend re-validates too)
    const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((formData.email || '').trim());
    if (!(formData.name || '').trim()) { toast.error('Please enter your name.'); return; }
    if (!emailOk) { toast.error('Please enter a valid email address.'); return; }
    if ((formData.phone || '').trim()) {
      const digits = (formData.phone || '').replace(/\D/g, '');
      if (!/^[0-9+\s()\-]+$/.test(formData.phone) || digits.length < 7) {
        toast.error('Please enter a valid phone number.'); return;
      }
    }
    if (!(formData.message || '').trim()) { toast.error('Please enter a message.'); return; }
    setSubmitting(true);
    try {
      await api.post('/contact-messages', { ...formData, source_url: window.location.href });
      toast.success('Message sent successfully! We\'ll respond within 24 hours.');
      setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast.error(err.message || 'Could not send your message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    { key: 'uk-address', icon: MapPin, title: 'UK OFFICE', details: (settings.address_uk || '').split('\n').filter(Boolean) },
    { key: 'uae-address', icon: MapPin, title: 'UAE OFFICE', details: (settings.address_uae || '').split('\n').filter(Boolean) },
    { key: 'telephone', icon: Phone, title: 'TELEPHONE', details: [
      settings.phone && { text: settings.phone, href: `tel:${settings.phone.replace(/[^+\d]/g, '')}` },
      settings.whatsapp && { text: `WhatsApp: ${settings.whatsapp}`, href: `https://wa.me/${settings.whatsapp.replace(/\D/g, '')}` },
    ].filter(Boolean) },
    { key: 'email', icon: Mail, title: 'EMAIL', details: [...new Set([settings.sales_email, settings.support_email].filter(Boolean))]
      .map(email => ({ text: email, href: `mailto:${email}` })) },
  ].filter(info => info.details.length);
  const contactAnswer = [settings.whatsapp && `WhatsApp: ${settings.whatsapp}.`, settings.phone && `Telephone: ${settings.phone}.`,
    settings.sales_email && `Email ${settings.sales_email} for project enquiries.`].filter(Boolean).join(' ') || 'Use the enquiry form to contact our team.';

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://lumibright.co.uk/' },
    { name: 'Contact', url: 'https://lumibright.co.uk/contact' }
  ]);

  // FAQPage schema — audit finding #27. Uses the most common specifier
  // questions we already answer on this page.
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do I request a full DIALux, IES or BIM file for a LUMIBRIGHT product?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Request available DIALux, IES photometric and BIM assets through the LUMIBRIGHT Trade Program at /trade. ${contactAnswer}`
        }
      },
      {
        "@type": "Question",
        "name": "Which markets does LUMIBRIGHT ship to?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "LUMIBRIGHT supplies architectural LED lighting to the United Kingdom, United Arab Emirates, Saudi Arabia and India, with approved distribution partners across the GCC and Europe."
        }
      },
      {
        "@type": "Question",
        "name": "Do LUMIBRIGHT products comply with UK Part L, CIBSE and BREEAM requirements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. Our indoor families are engineered to exceed the Part L (2021 uplift) efficacy targets and align to the CIBSE LG7, LG9, LG10 and LG12 guides. BREEAM Hea 01 and Hea 06 evidence packs are available under NDA to registered assessors. Full compliance summary at /standards."
        }
      },
      {
        "@type": "Question",
        "name": "Are LUMIBRIGHT outdoor fixtures rated for GCC climate?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes — outdoor and landscape families are rated Ta 45 °C to Ta 50 °C, with IP65 (façade) through IP68 (submersible) ingress protection, and comply with ESMA S.ELECTC.010 and the Dubai Green Building Code."
        }
      },
      {
        "@type": "Question",
        "name": "What is the fastest way to reach the LUMIBRIGHT specifier team?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": contactAnswer
        }
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Contact Us | Get in Touch with LUMIBRIGHT Lighting Specialists"
        description={`Contact LUMIBRIGHT for lighting project enquiries, technical support, or product information.${settings.sales_email ? ` Email ${settings.sales_email}.` : ''}`}
        keywords="contact lumibright, lighting enquiry, technical support, sales contact, UK lighting supplier contact, specification team"
        schema={[breadcrumbSchema, faqSchema]}
      />
      
      <div className="min-h-screen bg-white pt-[72px]" data-testid="contact-page">
      {/* Hero Section */}
      <section 
        className="relative py-32 text-white text-center overflow-hidden"
        data-testid="contact-hero"
      >
        <img alt="" src={optimizedSrc('/hero-banner-3.png', { w: 1024 })} srcSet={optimizedSrcSet('/hero-banner-3.png')} sizes="100vw" width="1024" height="1536" fetchPriority="high" className="absolute inset-0 w-full h-full object-cover object-center" data-testid="contact-hero-image" />
        <div className="absolute inset-0 bg-black/65"></div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="text-xs uppercase tracking-widest text-white/70 mb-6" style={{fontFamily: 'Inter, system-ui', fontWeight: 500, letterSpacing: '0.25em'}}>
            GET IN TOUCH
          </p>
          <h1 className="text-5xl sm:text-6xl lg:text-8xl leading-tight uppercase mb-6" style={{fontFamily: 'Satoshi, system-ui', letterSpacing: '-0.02em'}}>
            <span style={{fontWeight: 300}}>CONTACT </span>
            <span style={{fontWeight: 700}}>US</span>
          </h1>
          <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed" style={{fontFamily: 'Inter, system-ui', fontWeight: 400, lineHeight: 1.7}}>
            Speak with our specification team about your lighting project requirements
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="sr-only">How to reach the specification team</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="contact-settings-details">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={info.key} className="border border-gray-200" data-testid={`contact-${info.key}`}>
                  <CardContent className="p-6 text-center">
                    <Icon className="w-6 h-6 mx-auto mb-4 text-black" />
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.15em'}}>
                      {info.title}
                    </h3>
                    <div className="space-y-1">
                      {info.details.map((detail, i) => {
                        const isObj = detail && typeof detail === 'object';
                        const text = isObj ? detail.text : detail;
                        const href = isObj ? detail.href : null;
                        const isExternal = href && href.startsWith('http');
                        // Enlarged touch target: min 44px hit area on tappable
                        // items (audit finding — Contact page controls were 14 px).
                        return href ? (
                          <a
                            key={i}
                            href={href}
                            target={isExternal ? '_blank' : undefined}
                            rel={isExternal ? 'noopener noreferrer' : undefined}
                            className="block text-sm text-gray-700 hover:text-[#C89968] py-2 min-h-[44px] flex items-center justify-center transition-colors"
                            style={{ fontFamily: 'Inter, system-ui' }}
                            data-testid={`contact-link-${info.key}-${i}`}
                          >
                            {text}
                          </a>
                        ) : (
                          <p key={i} className="text-sm text-gray-700 break-words" data-testid={`contact-${info.key}-line-${i}`} style={{ fontFamily: 'Inter, system-ui' }}>
                            {text}
                          </p>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="mt-6 flex justify-center"><SocialLinks placement="contact" /></div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-10 text-center">
            <h2 className="text-3xl md:text-4xl font-semibold mb-3 uppercase" style={{fontFamily: 'Satoshi, system-ui', letterSpacing: '0.01em'}}>
              <span style={{fontWeight: 300}}>SEND US A </span>
              <span style={{fontWeight: 700}}>MESSAGE</span>
            </h2>
            <p className="text-sm text-gray-600 uppercase tracking-wider" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.15em'}}>
              WE'LL RESPOND WITHIN 24 HOURS
            </p>
          </div>

          <form onSubmit={handleSubmit} method="post" className="space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2 block" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.1em'}}>
                  FULL NAME *
                </label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Smith"
                  className="h-12"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2 block" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.1em'}}>
                  EMAIL ADDRESS *
                </label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                  className="h-12"
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2 block" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.1em'}}>
                  COMPANY / ORGANIZATION
                </label>
                <Input
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Company Name"
                  className="h-12"
                  data-testid="contact-company-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2 block" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.1em'}}>
                  PHONE NUMBER
                </label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+44 20 1234 5678"
                  className="h-12"
                  data-testid="contact-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2 block" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.1em'}}>
                SUBJECT *
              </label>
              <Input
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="Project Enquiry / Technical Support / General Question"
                className="h-12"
                data-testid="contact-subject-input"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-700 mb-2 block" style={{fontFamily: 'Inter, system-ui', letterSpacing: '0.1em'}}>
                MESSAGE *
              </label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                placeholder="Tell us about your project requirements, technical questions, or how we can help..."
                rows={6}
                className="resize-none"
                data-testid="contact-message-input"
              />
            </div>

            <div className="text-center pt-4">
              <Button 
                type="submit" 
                size="lg"
                disabled={submitting}
                className="uppercase tracking-wider font-semibold min-w-[200px]"
                data-testid="contact-submit-btn"
              >
                {submitting ? 'SENDING...' : (
                  <>
                    SEND MESSAGE
                    <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-gray-500 mt-4" style={{fontFamily: 'Inter, system-ui'}}>
              By submitting this form, you agree to our privacy policy and terms of service.
            </p>
          </form>
        </div>
      </section>

      {/* Visible FAQ — mirrors the FAQPage JSON-LD above so the schema
          matches the DOM, per Google's structured-data policy. */}
      <section className="py-16 md:py-20 border-t border-gray-100" data-testid="contact-faq-section">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-semibold uppercase mb-3" style={{ fontFamily: 'Satoshi, system-ui', letterSpacing: '0.02em' }}>
            <span style={{ fontWeight: 300 }}>Frequently </span>
            <span style={{ fontWeight: 700 }}>Asked</span>
          </h2>
          <p className="text-sm text-gray-600 mb-10" style={{ fontFamily: 'Inter, system-ui' }}>
            Five of the questions our specifier team hears most often — with pointers to the underlying documentation.
          </p>
          <div className="divide-y divide-gray-200">
            {faqSchema.mainEntity.map((q, i) => (
              <FaqRow
                key={i}
                question={q.name}
                answer={q.acceptedAnswer.text}
                testId={`contact-faq-${i}`}
                defaultOpen={i === 0}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Map or Additional Info Section */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-semibold mb-4 uppercase" style={{fontFamily: 'Satoshi, system-ui', letterSpacing: '0.02em'}}>
            <span style={{fontWeight: 300}}>DISTRIBUTION </span>
            <span style={{fontWeight: 700}}>NETWORK</span>
          </h3>
          <p className="text-base text-gray-700 leading-relaxed max-w-3xl mx-auto" style={{fontFamily: 'Inter, system-ui', lineHeight: 1.7}}>
            With distribution partners across Europe, Middle East, Asia, and Americas, <span style={{fontWeight: 300}}>LUMI</span><span style={{fontWeight: 700}}>BRIGHT</span> ensures rapid delivery and local technical support for projects worldwide. Contact us to find your nearest distributor or discuss direct procurement for large-scale installations.
          </p>
        </div>
      </section>
    </div>
    </>
  );
}

function FaqRow({ question, answer, testId, defaultOpen = false }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="py-5" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-4 text-left group"
      >
        <h3 className="text-base md:text-lg font-medium text-[#1A1A1A] group-hover:text-[#C89968] transition-colors" style={{ fontFamily: 'Satoshi, system-ui' }}>
          {question}
        </h3>
        <ChevronDown className={`w-5 h-5 mt-1 text-gray-500 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <p className="text-sm text-gray-700 leading-relaxed mt-3 pr-9" style={{ fontFamily: 'Inter, system-ui', lineHeight: 1.7 }} data-testid={`${testId}-answer`}>
          {answer}
        </p>
      )}
    </div>
  );
}
